/**
 * Field primitives and the per-stage editor, shared by the category settings
 * tabs and the new-category wizard so an existing category and a new one are
 * configured through exactly the same controls.
 */
import type { KnowledgeStage, KnowledgeStageMode, KnowledgeStrictness } from './api.js';

export const Field = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div className="mb-4">
    <p className="text-sm font-medium text-ink">{label}</p>
    {hint && <p className="mb-1 mt-0.5 text-xs text-ink-muted">{hint}</p>}
    {children}
  </div>
);

export const TextArea = ({
  value,
  onChange,
  rows = 3,
  label,
  mono = false,
  disabled = false,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  label: string;
  mono?: boolean;
  disabled?: boolean;
  placeholder?: string;
}) => (
  <textarea
    value={value}
    aria-label={label}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    rows={rows}
    className={`mt-1 w-full rounded border border-outline-strong bg-surface p-2 text-sm text-ink ${
      mono ? 'font-mono text-xs' : ''
    }`}
  />
);

/**
 * Two answers to one question: does the AI write this, or does your team?
 *
 * Single-pass (LLM_SYNTHESIS) is deliberately NOT offered. Its only effect is
 * skipping the review that catches unsourced values, contradictions and missing
 * preconditions — the failures that matter most in content someone acts on. A
 * control whose function is "make the output worse to save a little money" is
 * one a non-technical user gets wrong. The value still exists in the API for a
 * measured decision later; it just isn't a dial here.
 */
export const MODES: Array<{ id: KnowledgeStageMode; label: string; blurb: string }> = [
  {
    id: 'LLM_DEBATE',
    label: 'The AI writes it',
    blurb: 'A second AI checks the draft against your documents, then the writer revises it once.',
  },
  {
    id: 'MANUAL',
    label: 'Your team writes it',
    blurb: 'No AI call is made. You add and write each entry yourself.',
  },
];

export const STRICTNESS: Array<{ id: KnowledgeStrictness; label: string; blurb: string }> = [
  {
    id: 'STRICT',
    label: 'Only my documents',
    blurb: 'The AI uses only what you uploaded. Where they are silent, it says so.',
  },
  {
    id: 'OPEN',
    label: 'May fill gaps',
    blurb: 'The AI may use general knowledge for structure — never for specific values.',
  },
];

export const Choice = <T extends string>({
  options,
  value,
  onChange,
  name,
  disabled = false,
}: {
  options: Array<{ id: T; label: string; blurb: string }>;
  value: T;
  onChange: (v: T) => void;
  name: string;
  disabled?: boolean;
}) => (
  <div className="mt-1 grid gap-2 sm:grid-cols-2">
    {options.map((o) => (
      <button
        key={o.id}
        type="button"
        role="radio"
        aria-checked={value === o.id}
        aria-label={`${name}: ${o.label}`}
        onClick={() => onChange(o.id)}
        disabled={disabled}
        className={`rounded-lg border p-3 text-left transition-colors ${
          value === o.id
            ? 'border-primary bg-surface-hover'
            : 'border-outline hover:border-outline-strong'
        } ${disabled ? 'cursor-default opacity-70' : ''}`}
      >
        <span className="block text-sm font-medium text-ink">{o.label}</span>
        <span className="mt-0.5 block text-xs text-ink-muted">{o.blurb}</span>
      </button>
    ))}
  </div>
);

/**
 * Per-stage wording. "Instructions" alone collides with the category briefing a
 * user has just written — they reasonably think they've already done this. The
 * distinction these labels carry: the briefing is what the AI KNOWS about the
 * equipment (once per category); this is what it DOES in this one step.
 */
export const STAGE_COPY: Record<'list' | 'item', { label: string; hint: string }> = {
  list: {
    label: 'How should the AI decide what to cover?',
    hint: 'The task for this step — how to choose and rank entries, how specific to be, roughly how many. Not facts about the equipment: those are in the briefing, and the AI already has them.',
  },
  item: {
    label: 'How should each guide be written?',
    hint: 'The shape of one finished guide — what to lead with, what order to work through, how to end. Not facts about the equipment: those are in the briefing, and the AI already has them.',
  },
};

export const StageEditor = ({
  title,
  blurb,
  stage,
  onChange,
  stageName,
  stageKind,
  readOnly = false,
}: {
  title: string;
  blurb: string;
  stage: KnowledgeStage;
  onChange: (s: KnowledgeStage) => void;
  stageName: string;
  /** Selects the per-stage wording (see STAGE_COPY). */
  stageKind: 'list' | 'item';
  readOnly?: boolean;
}) => (
  <div className="rounded-lg border border-outline bg-surface-elevated p-4">
    <p className="text-sm font-semibold text-ink">{title}</p>
    <p className="mb-3 text-xs text-ink-muted">{blurb}</p>

    <Field label="Who writes this?">
      <Choice
        options={MODES}
        value={stage.mode}
        onChange={(mode) => onChange({ ...stage, mode })}
        name={`${stageName} writer`}
        disabled={readOnly}
      />
    </Field>

    {stage.mode !== 'MANUAL' && (
      <>
        <Field label="Sourcing">
          <Choice
            options={STRICTNESS}
            value={stage.strictness}
            onChange={(strictness) => onChange({ ...stage, strictness })}
            name={`${stageName} sourcing`}
            disabled={readOnly}
          />
        </Field>
        <Field label={STAGE_COPY[stageKind].label} hint={STAGE_COPY[stageKind].hint}>
          <TextArea
            value={stage.prompt}
            onChange={(prompt) => onChange({ ...stage, prompt })}
            label={`${stageName} instructions`}
            rows={8}
            mono
            disabled={readOnly}
          />
        </Field>
      </>
    )}
  </div>
);
